import { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Circle, Marker, Popup, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { feetToMeters } from "../../lib/utils";

// Fix Leaflet default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

const createNumberedIcon = (number, color = "#0F172A") => {
  return L.divIcon({
    className: "custom-marker",
    html: `<div style="
      background-color: ${color};
      color: white;
      width: 32px;
      height: 32px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: bold;
      font-size: 14px;
      border: 3px solid white;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    ">${number}</div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  });
};

const guardIcon = L.divIcon({
  className: "guard-marker",
  html: `<div style="
    background-color: #F97316;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: 3px solid white;
    box-shadow: 0 0 10px rgba(249, 115, 22, 0.6);
  "></div>`,
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

const MapClickHandler = ({ onMapClick }) => {
  useMapEvents({
    click: (e) => {
      if (onMapClick) {
        onMapClick(e.latlng);
      }
    },
  });
  return null;
};

const FitBounds = ({ checkpoints }) => {
  const map = useMap();
  
  useEffect(() => {
    if (checkpoints && checkpoints.length > 0 && map) {
      try {
        const bounds = L.latLngBounds(
          checkpoints.map((cp) => [cp.latitude, cp.longitude])
        );
        // Verificar que el mapa esté listo antes de hacer fitBounds
        if (map.getContainer() && map.getContainer().offsetParent !== null) {
          map.fitBounds(bounds, { padding: [50, 50] });
        }
      } catch (error) {
        console.error("Error ajustando bounds del mapa:", error);
      }
    }
  }, [checkpoints, map]);
  
  return null;
};

const FocusOnSelectedCheckpoint = ({ selectedCheckpoint, checkpoints }) => {
  const map = useMap();
  
  useEffect(() => {
    if (selectedCheckpoint && map) {
      try {
        const checkpointLatLng = L.latLng(selectedCheckpoint.latitude, selectedCheckpoint.longitude);
        const bounds = map.getBounds();
        
        // Verificar que el mapa esté listo
        if (bounds && map.getContainer() && map.getContainer().offsetParent !== null) {
          // Verificar si el checkpoint está visible en el viewport actual
          const isVisible = bounds.contains(checkpointLatLng);
          
          if (!isVisible) {
            // Si no está visible, centrar el mapa en el checkpoint con zoom 15
            map.setView(checkpointLatLng, 15, { animate: true });
          }
        }
      } catch (error) {
        console.error("Error enfocando checkpoint:", error);
      }
    }
  }, [selectedCheckpoint, map]);
  
  return null;
};

const CenterMap = ({ center }) => {
  const map = useMap();
  
  useEffect(() => {
    if (center && map && Array.isArray(center) && center.length === 2) {
      try {
        if (map.getContainer() && map.getContainer().offsetParent !== null) {
          map.setView(center, map.getZoom(), { animate: false });
        }
      } catch (error) {
        console.error("Error centrando mapa:", error);
      }
    }
  }, [center, map]);
  
  return null;
};

export const CheckpointMap = ({
  checkpoints = [],
  selectedCheckpoint,
  onCheckpointClick,
  onMapClick,
  guardPosition,
  showRadius = true,
  height = "400px",
  interactive = true,
  completedCheckpoints = [],
}) => {
  const [mapCenter, setMapCenter] = useState([18.478973, -69.967669]); // República Dominicana default
  const [userLocation, setUserLocation] = useState(null);
  const mapRef = useRef(null);

  // Obtener ubicación actual del usuario al montar el componente
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          setUserLocation({ latitude: lat, longitude: lng });
        },
        (error) => {
          // Si falla la geolocalización, intentar obtener ubicación aproximada por IP
          fetch("https://ipapi.co/json/")
            .then((response) => response.json())
            .then((data) => {
              if (data.latitude && data.longitude) {
                setUserLocation({
                  latitude: parseFloat(data.latitude),
                  longitude: parseFloat(data.longitude),
                });
              }
            })
            .catch((err) => {
              console.error("Error obteniendo ubicación por IP:", err);
            });
        },
        {
          enableHighAccuracy: false,
          timeout: 10000,
          maximumAge: 60000,
        }
      );
    } else {
      // Si no hay geolocalización, intentar obtener ubicación por IP
      fetch("https://ipapi.co/json/")
        .then((response) => response.json())
        .then((data) => {
          if (data.latitude && data.longitude) {
            setUserLocation({
              latitude: parseFloat(data.latitude),
              longitude: parseFloat(data.longitude),
            });
          }
        })
        .catch((err) => {
          console.error("Error obteniendo ubicación por IP:", err);
        });
    }
  }, []);

  useEffect(() => {
    // Solo actualizar el centro si no hay checkpoints, para evitar conflictos con FitBounds
    if (checkpoints.length === 0) {
      if (guardPosition) {
        setMapCenter([guardPosition.latitude, guardPosition.longitude]);
      } else if (userLocation) {
        setMapCenter([userLocation.latitude, userLocation.longitude]);
      }
    }
  }, [checkpoints.length, guardPosition, userLocation]);

  return (
    <div className="rounded-lg overflow-hidden border border-slate-200" style={{ height }}>
      <MapContainer
        center={mapCenter}
        zoom={15}
        style={{ height: "100%", width: "100%" }}
        ref={mapRef}
        scrollWheelZoom={interactive}
        dragging={interactive}
        zoomControl={interactive}
      >
        <TileLayer
          attribution='&copy; <a href="https://carto.com/">CARTO</a>'
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
        />
        
        {interactive && onMapClick && <MapClickHandler onMapClick={onMapClick} />}
        {checkpoints.length > 0 && <FitBounds checkpoints={checkpoints} />}
        {checkpoints.length === 0 && (guardPosition || userLocation) && (
          <CenterMap center={mapCenter} />
        )}
        {selectedCheckpoint && <FocusOnSelectedCheckpoint selectedCheckpoint={selectedCheckpoint} checkpoints={checkpoints} />}
        
        {checkpoints.map((checkpoint, index) => {
          const isCompleted = completedCheckpoints.includes(checkpoint.id);
          const isSelected = selectedCheckpoint?.id === checkpoint.id;
          
          // Determinar el color: verde si está completado, naranja si está seleccionado, negro por defecto
          let markerColor = "#0F172A"; // Negro por defecto
          if (isCompleted) {
            markerColor = "#10B981"; // Verde para completados
          } else if (isSelected) {
            markerColor = "#F97316"; // Naranja para seleccionado
          }
          
          return (
            <div key={checkpoint.id}>
              {showRadius && (
                <Circle
                  center={[checkpoint.latitude, checkpoint.longitude]}
                  radius={feetToMeters(checkpoint.radius_feet)}
                  pathOptions={{
                    color: markerColor,
                    fillColor: markerColor,
                    fillOpacity: 0.1,
                    weight: 2,
                  }}
                />
              )}
              <Marker
                position={[checkpoint.latitude, checkpoint.longitude]}
                icon={createNumberedIcon(
                  index + 1,
                  markerColor
                )}
                eventHandlers={{
                  click: () => onCheckpointClick && onCheckpointClick(checkpoint),
                }}
              >
                <Popup>
                  <div className="p-2">
                    <h3 className="font-bold text-slate-900">{checkpoint.name}</h3>
                    <p className="text-sm text-slate-600">{checkpoint.description}</p>
                    {isCompleted && (
                      <p className="text-xs text-green-600 font-semibold mt-1">
                        ✓ Completado
                      </p>
                    )}
                    <p className="text-xs font-mono text-slate-500 mt-1">
                      {checkpoint.latitude.toFixed(6)}, {checkpoint.longitude.toFixed(6)}
                    </p>
                    <p className="text-xs text-slate-500">
                      Radio: {checkpoint.radius_feet} pies
                    </p>
                  </div>
                </Popup>
              </Marker>
            </div>
          );
        })}
        
        {guardPosition && (
          <Marker
            position={[guardPosition.latitude, guardPosition.longitude]}
            icon={guardIcon}
          >
            <Popup>
              <div className="p-2">
                <h3 className="font-bold text-orange-600">Tu Ubicación</h3>
                <p className="text-xs font-mono text-slate-500">
                  {guardPosition.latitude.toFixed(6)}, {guardPosition.longitude.toFixed(6)}
                </p>
              </div>
            </Popup>
          </Marker>
        )}
      </MapContainer>
    </div>
  );
};
